import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Input,
  Select,
  Heading,
  VStack,
  HStack,
  useToast,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  Badge,
  List,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, CheckIcon, EditIcon } from '@chakra-ui/icons';
import { GET_MATCH, GET_MATCH_FORMAT, ADD_MATCH_RESULT, GET_MATCH_RESULTS, UPDATE_MATCH, QUERY_MATCHES, UPDATE_MATCH_RESULT, DELETE_MATCH_RESULT } from '../../Queries';
import { Footer, WithSubnavigation } from '../../Components';

// Helper component to display tiebreaker values safely
const TiebreakerDisplay = ({ format, details }) => {
  try {
    // Determine which tiebreaker fields to display based on format rules
    const tiebreakerFields = [];
    
    if (format?.tiebreaker_rules?.enabled && Array.isArray(format.tiebreaker_rules.steps)) {
      format.tiebreaker_rules.steps.forEach(step => {
        if (step && step.type === 'AGGREGATE_SCORE_FIELD') {
          // Auto-calculated fields from stage scores
          if (step.field === 'x_count' && details.total_x_count !== undefined && details.total_x_count !== null) {
            tiebreakerFields.push({ label: 'X', value: details.total_x_count });
          } else if (step.field === 'inner_tens' && details.total_inner_tens !== undefined && details.total_inner_tens !== null) {
            tiebreakerFields.push({ label: '10s', value: details.total_inner_tens });
          } else if (step.field === 'clean_targets' && details.total_clean_targets !== undefined && details.total_clean_targets !== null) {
            tiebreakerFields.push({ label: 'Clean', value: details.total_clean_targets });
          }
        } else if (step && step.type === 'SHOOTOFF') {
          // Shootoff scores from tiebreaker_data
          const shootoffField = `shootoff_${step.order}_score`;
          const shootoffValue = details.tiebreaker_data?.[shootoffField];
          if (shootoffValue !== undefined && shootoffValue !== null) {
            tiebreakerFields.push({ 
              label: `${step.label || 'SO'}`, 
              value: `${shootoffValue}/${step.target_count || '?'}` 
            });
          }
        } else if (step && step.type === 'CUSTOM_FIELD') {
          // Custom fields from tiebreaker_data
          const customFields = details.tiebreaker_data?.custom_fields;
          let fieldValue = null;
          
          if (Array.isArray(customFields)) {
            // Array format from GraphQL: [{field: "name", value: 10}, ...]
            const field = customFields.find(f => f.field === step.field);
            fieldValue = field?.value;
          } else if (customFields && typeof customFields === 'object') {
            // Object format from staged results: {field_name: value, ...}
            fieldValue = customFields[step.field];
          }
          
          if (fieldValue !== undefined && fieldValue !== null) {
            tiebreakerFields.push({ 
              label: step.label || step.field, 
              value: fieldValue 
            });
          }
        }
      });
    }
    
    if (tiebreakerFields.length > 0) {
      return (
        <VStack spacing={0} align="start">
          {tiebreakerFields.map((field, idx) => (
            <Text key={idx} fontSize="sm">{field.label}: {field.value}</Text>
          ))}
        </VStack>
      );
    }
    
    return <Text fontSize="sm" color="gray.400">-</Text>;
  } catch (error) {
    console.error('Error rendering tiebreaker display:', error);
    return <Text fontSize="sm" color="red.500">Error</Text>;
  }
};

export const ResultsEntry = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [stagedResults, setStagedResults] = useState([]); // Results staged for bulk save
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [tiebreakerModalResult, setTiebreakerModalResult] = useState(null);
  const [tiebreakerFormData, setTiebreakerFormData] = useState(null);

  // Query match details
  const { data: matchData, loading: matchLoading } = useQuery(GET_MATCH, {
    variables: { match_id: matchId },
  });

  // Query existing results (only saved results from database)
  const { data: resultsData, loading: resultsLoading, refetch: refetchResults } = useQuery(
    GET_MATCH_RESULTS,
    {
      variables: { match_id: matchId },
      skip: !matchId,
      onCompleted: (data) => {
        console.log('GET_MATCH_RESULTS completed:', data);
        if (data?.getMatchResults) {
          console.log('First result:', data.getMatchResults[0]);
          if (data.getMatchResults[0]?.result_details?.stage_scores) {
            console.log('First stage score:', data.getMatchResults[0].result_details.stage_scores[0]);
          }
        }
      },
      onError: (error) => {
        console.error('GET_MATCH_RESULTS error:', error);
      }
    }
  );

  // Query match format
  const formatId = matchData?.getMatch?.match_details?.match_format_id;
  const { data: formatData } = useQuery(GET_MATCH_FORMAT, {
    variables: { format_id: formatId },
    skip: !formatId,
    onCompleted: (data) => {
      console.log('GET_MATCH_FORMAT completed:', data);
      if (data?.getMatchFormat?.format_details?.stages) {
        console.log('Stages:', data.getMatchFormat.format_details.stages);
      }
    },
    onError: (error) => {
      console.error('GET_MATCH_FORMAT error:', error);
    }
  });

  const [addResult, { loading: adding }] = useMutation(ADD_MATCH_RESULT, {
    onError: (error) => {
      toast({
        title: 'Error adding result',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const [bulkAddResults, { loading: bulkAdding }] = useMutation(
    gql`
      mutation BulkAddMatchResults($match_id: String!, $results: [MatchResultDetailsInput!]!) {
        bulkAddMatchResults(match_id: $match_id, results: $results) {
          result_id
          result_details {
            participant_name
            aggregate_score
          }
        }
      }
    `,
    {
      onCompleted: () => {
        toast({
          title: 'Results saved successfully',
          description: `${stagedResults.length} results saved to database`,
          status: 'success',
          duration: 3000,
        });
        setStagedResults([]);
        refetchResults();
      },
      onError: (error) => {
        toast({
          title: 'Error saving results',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      },
    }
  );

  const [updateMatchStatus] = useMutation(UPDATE_MATCH, {
    onCompleted: () => {
      toast({
        title: 'Match status updated',
        status: 'success',
        duration: 2000,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating match status',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    refetchQueries: [
      { query: GET_MATCH, variables: { match_id: matchId } },
      'QueryMatches', // Refetch all QueryMatches queries
    ],
  });

  const [finalizeMatch, { loading: finalizing }] = useMutation(UPDATE_MATCH, {
    onCompleted: () => {
      toast({
        title: 'Match finalized successfully',
        description: 'All results saved and match marked as COMPLETED',
        status: 'success',
        duration: 3000,
      });
      refetchResults();
    },
    onError: (error) => {
      toast({
        title: 'Error finalizing match',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
    refetchQueries: [
      { query: GET_MATCH, variables: { match_id: matchId } },
      'QueryMatches', // Refetch all QueryMatches queries
    ],
  });

  const [updateResult] = useMutation(UPDATE_MATCH_RESULT, {
    onCompleted: () => {
      toast({
        title: 'Result updated',
        status: 'success',
        duration: 2000,
      });
      handleCloseEditModal();
      refetchResults();
    },
    onError: (error) => {
      toast({
        title: 'Error updating result',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const [deleteResult] = useMutation(DELETE_MATCH_RESULT, {
    onCompleted: () => {
      toast({
        title: 'Result deleted',
        status: 'success',
        duration: 2000,
      });
      refetchResults();
    },
    onError: (error) => {
      toast({
        title: 'Error deleting result',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const match = matchData?.getMatch;
  const savedResults = resultsData?.getMatchResults || []; // Results from database
  const allResults = [...savedResults, ...stagedResults]; // Combine saved + staged
  const format = formatData?.getMatchFormat?.format_details;
  const stages = format?.stages || [];

  const isUpcoming = match?.match_details?.match_status === 'UPCOMING';
  const isInProgress = match?.match_details?.match_status === 'IN_PROGRESS';
  const isCompleted = match?.match_details?.match_status === 'COMPLETED';
  const canEnterScores = isInProgress; // Only allow score entry when IN_PROGRESS

  // Group and rank results by division and category
  const rankedResults = useMemo(() => {
    if (allResults.length === 0) return [];

    // Group by division and category
    const groups = {};
    allResults.forEach(result => {
      const details = result.result_details || result; // Handle both saved and staged formats
      const division = details.division || 'No Division';
      const category = details.category || 'No Category';
      const key = `${division}|${category}`;
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    });

    // Sort each group by score (descending) and apply tie-breakers
    const rankedList = [];
    Object.keys(groups).forEach(key => {
      const group = groups[key];
      
      // For MATCH_POINTS, calculate match points for each competitor
      if (format?.stage_aggregation_method === 'MATCH_POINTS') {
        // First, find the maximum hit factor for each stage
        const stageMaxHF = {};
        const stagePoints = {};
        
        stages.forEach(stage => {
          let maxHF = 0;
          const stagePointsValue = parseFloat(stage.stage_points) || 100;
          stagePoints[stage.stage_name] = stagePointsValue;
          
          group.forEach(result => {
            const details = result.result_details || result;
            const stageScore = details.stage_scores?.find(s => s.stage_name === stage.stage_name);
            if (stageScore) {
              const hitFactor = stageScore.calculated_value || 0;
              if (hitFactor > maxHF) {
                maxHF = hitFactor;
              }
            }
          });
          
          stageMaxHF[stage.stage_name] = maxHF;
        });
        
        // Calculate match points for each competitor
        group.forEach(result => {
          const details = result.result_details || result;
          let totalMatchPoints = 0;
          const matchPointsBreakdown = [];
          
          stages.forEach(stage => {
            const stageScore = details.stage_scores?.find(s => s.stage_name === stage.stage_name);
            const hitFactor = stageScore?.calculated_value || 0;
            const maxHF = stageMaxHF[stage.stage_name];
            const stagePointsValue = stagePoints[stage.stage_name];
            
            let matchPoints = 0;
            if (maxHF > 0) {
              matchPoints = (hitFactor / maxHF) * stagePointsValue;
            }
            
            totalMatchPoints += matchPoints;
            matchPointsBreakdown.push({
              stage_name: stage.stage_name,
              hit_factor: hitFactor,
              match_points: matchPoints,
              is_winner: hitFactor === maxHF && maxHF > 0
            });
          });
          
          // Store calculated match points in the result
          if (result.result_details) {
            result.result_details.aggregate_score = totalMatchPoints;
            result.result_details.match_points_breakdown = matchPointsBreakdown;
          } else {
            result.aggregate_score = totalMatchPoints;
            result.match_points_breakdown = matchPointsBreakdown;
          }
        });
      }
      
      // Sort by aggregate_score descending (higher is better)
      group.sort((a, b) => {
        const detailsA = a.result_details || a;
        const detailsB = b.result_details || b;
        const scoreA = detailsA.aggregate_score;
        const scoreB = detailsB.aggregate_score;
        
        // If scores are different, sort by score
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }
        
        // Scores are tied - apply tie-breaker rules if available
        if (format?.tiebreaker_rules?.enabled && format.tiebreaker_rules.steps?.length > 0) {
          for (const step of format.tiebreaker_rules.steps) {
            if (step.type === 'AGGREGATE_SCORE_FIELD') {
              const fieldName = `total_${step.field}`;
              const valueA = detailsA[fieldName] || 0;
              const valueB = detailsB[fieldName] || 0;
              
              if (valueA !== valueB) {
                // Apply direction (DESC = higher wins, ASC = lower wins)
                return step.direction === 'DESC' ? valueB - valueA : valueA - valueB;
              }
            } else if (step.type === 'STAGE_SCORE') {
              // Find the specific stage score
              const stageA = detailsA.stage_scores?.find(s => s.stage_name === step.stage_id);
              const stageB = detailsB.stage_scores?.find(s => s.stage_name === step.stage_id);
              const valueA = stageA?.calculated_value || 0;
              const valueB = stageB?.calculated_value || 0;
              
              if (valueA !== valueB) {
                return step.direction === 'DESC' ? valueB - valueA : valueA - valueB;
              }
            } else if (step.type === 'SHOOTOFF') {
              // Check if shootoff data exists
              const shootoffField = `shootoff_${step.order}_score`;
              const valueA = detailsA.tiebreaker_data?.[shootoffField];
              const valueB = detailsB.tiebreaker_data?.[shootoffField];
              
              if (valueA !== undefined && valueB !== undefined && valueA !== valueB) {
                return valueB - valueA; // Higher shootoff score wins
              }
            } else if (step.type === 'CUSTOM_FIELD') {
              // custom_fields can be either array or object depending on source
              const customFieldsA = detailsA.tiebreaker_data?.custom_fields;
              const customFieldsB = detailsB.tiebreaker_data?.custom_fields;
              
              let valueA = 0;
              let valueB = 0;
              
              // Handle array format (from GraphQL)
              if (Array.isArray(customFieldsA)) {
                const fieldA = customFieldsA.find(f => f.field === step.field);
                valueA = fieldA?.value || 0;
              } else if (customFieldsA && typeof customFieldsA === 'object') {
                // Handle object format (from staged results)
                valueA = customFieldsA[step.field] || 0;
              }
              
              if (Array.isArray(customFieldsB)) {
                const fieldB = customFieldsB.find(f => f.field === step.field);
                valueB = fieldB?.value || 0;
              } else if (customFieldsB && typeof customFieldsB === 'object') {
                valueB = customFieldsB[step.field] || 0;
              }
              
              if (valueA !== valueB) {
                return step.direction === 'DESC' ? valueB - valueA : valueA - valueB;
              }
            }
            // SHARED_PLACEMENT doesn't affect sorting, just rank display
          }
        }
        
        // Still tied after all tie-breakers
        return 0;
      });
      
      // Assign ranks within the group, handling ties
      let currentRank = 1;
      for (let i = 0; i < group.length; i++) {
        const result = group[i];
        const details = result.result_details || result;
        
        // Check if this result is tied with the previous one
        let isTied = false;
        let rankNote = null;
        
        if (i > 0) {
          const prevResult = group[i - 1];
          const prevDetails = prevResult.result_details || prevResult;
          
          if (details.aggregate_score === prevDetails.aggregate_score) {
            isTied = true;
            
            // Check if tie was broken by tie-breaker rules
            if (format?.tiebreaker_rules?.enabled && format.tiebreaker_rules.steps?.length > 0) {
              for (const step of format.tiebreaker_rules.steps) {
                if (step.type === 'AGGREGATE_SCORE_FIELD') {
                  const fieldName = `total_${step.field}`;
                  const valueA = details[fieldName] || 0;
                  const valueB = prevDetails[fieldName] || 0;
                  
                  if (valueA !== valueB) {
                    // Tie was broken - add rank note
                    rankNote = `${valueA} ${step.label}`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'STAGE_SCORE') {
                  const stageA = details.stage_scores?.find(s => s.stage_name === step.stage_id);
                  const valueA = stageA?.calculated_value || 0;
                  
                  const stageB = prevDetails.stage_scores?.find(s => s.stage_name === step.stage_id);
                  const valueB = stageB?.calculated_value || 0;
                  
                  if (valueA !== valueB) {
                    rankNote = `${valueA} (${step.label})`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'SHOOTOFF') {
                  const shootoffField = `shootoff_${step.order}_score`;
                  const valueA = details.tiebreaker_data?.[shootoffField];
                  const valueB = prevDetails.tiebreaker_data?.[shootoffField];
                  
                  if (valueA !== undefined && valueB !== undefined && valueA !== valueB) {
                    rankNote = `${valueA}/${step.target_count || '?'} SO`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'CUSTOM_FIELD') {
                  // custom_fields can be either array or object depending on source
                  const customFieldsA = details.tiebreaker_data?.custom_fields;
                  const customFieldsB = prevDetails.tiebreaker_data?.custom_fields;
                  
                  let valueA = 0;
                  let valueB = 0;
                  
                  // Handle array format (from GraphQL)
                  if (Array.isArray(customFieldsA)) {
                    const fieldA = customFieldsA.find(f => f.field === step.field);
                    valueA = fieldA?.value || 0;
                  } else if (customFieldsA && typeof customFieldsA === 'object') {
                    // Handle object format (from staged results)
                    valueA = customFieldsA[step.field] || 0;
                  }
                  
                  if (Array.isArray(customFieldsB)) {
                    const fieldB = customFieldsB.find(f => f.field === step.field);
                    valueB = fieldB?.value || 0;
                  } else if (customFieldsB && typeof customFieldsB === 'object') {
                    valueB = customFieldsB[step.field] || 0;
                  }
                  
                  if (valueA !== valueB) {
                    rankNote = `${valueA} ${step.label}`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'SHARED_PLACEMENT') {
                  // Explicitly mark as tied
                  rankNote = step.label || 'Tied';
                  isTied = true;
                  break;
                }
              }
            }
          }
        }
        
        // If not tied with previous, increment rank
        if (!isTied && i > 0) {
          currentRank = i + 1;
        }
        
        rankedList.push({
          ...result,
          rank: isTied ? `T${currentRank}` : currentRank,
          rankNote: rankNote,
          divisionCategory: key
        });
      }
    });

    // Sort the final list by division, category, then rank
    rankedList.sort((a, b) => {
      const detailsA = a.result_details || a;
      const detailsB = b.result_details || b;
      const divA = detailsA.division || 'No Division';
      const divB = detailsB.division || 'No Division';
      const catA = detailsA.category || 'No Category';
      const catB = detailsB.category || 'No Category';
      
      if (divA !== divB) return divA.localeCompare(divB);
      if (catA !== catB) return catA.localeCompare(catB);
      
      // Extract numeric rank for sorting (handle "T1" format)
      const rankA = typeof a.rank === 'string' ? parseInt(a.rank.replace('T', '')) : a.rank;
      const rankB = typeof b.rank === 'string' ? parseInt(b.rank.replace('T', '')) : b.rank;
      return rankA - rankB;
    });

    return rankedList;
  }, [allResults, format]);

  // Group results by division/category for display
  const resultsByDivisionCategory = useMemo(() => {
    const groups = {};
    rankedResults.forEach(result => {
      const key = result.divisionCategory;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    });
    return groups;
  }, [rankedResults]);

  // Extract unique participants from existing results (both saved and staged)
  const uniqueParticipants = useMemo(() => {
    const participantMap = new Map();
    allResults.forEach(result => {
      const details = result.result_details || result;
      const email = details.participant_email;
      const name = details.participant_name;
      if (email && name && !participantMap.has(email)) {
        participantMap.set(email, { name, email });
      }
    });
    return Array.from(participantMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [allResults]);

  function createEmptyRow() {
    const row = {
      id: Date.now() + Math.random(),
      participant_name: '',
      participant_email: '',
      division: '',
      category: '',
      stages: {},
    };
    
    if (stages && stages.length > 0) {
      stages.forEach(stage => {
        if (format?.stage_aggregation_method === 'MATCH_POINTS') {
          // For MATCH_POINTS, store both points and time
          row.stages[stage.stage_name] = { points: '', time: '' };
        } else if (stage.scoring_primitive === 'SCORING_RING' && (stage.parameters?.x_count || stage.parameters?.inner_tens || stage.parameters?.clean_targets)) {
          // For SCORING_RING with detailed scoring, store value plus optional fields
          row.stages[stage.stage_name] = {
            value: '',
            x_count: stage.parameters?.x_count ? '' : undefined,
            inner_tens: stage.parameters?.inner_tens ? '' : undefined,
            clean_targets: stage.parameters?.clean_targets ? '' : undefined,
          };
        } else {
          // For simple formats, just store the value
          row.stages[stage.stage_name] = '';
        }
      });
    }
    
    return row;
  }

  // Initialize rows when stages are loaded
  useEffect(() => {
    if (stages && stages.length > 0 && rows.length === 0) {
      setRows([createEmptyRow()]);
    }
  }, [stages]);  const addRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const updateRow = (id, field, value) => {
    setRows(rows.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
    
    // Only show suggestions when user has typed at least 2 characters
    if (field === 'participant_name') {
      setFocusedRowId(id);
      setShowSuggestions(value.length >= 2);
    }
  };

  const selectParticipant = (rowId, participant) => {
    setRows(rows.map(row => 
      row.id === rowId 
        ? { ...row, participant_name: participant.name, participant_email: participant.email }
        : row
    ));
    setShowSuggestions(false);
    setFocusedRowId(null);
  };

  const getFilteredSuggestions = (rowId) => {
    const row = rows.find(r => r.id === rowId);
    if (!row || !row.participant_name || row.participant_name.trim().length < 2) {
      return [];
    }
    
    const searchTerm = row.participant_name.toLowerCase();
    return uniqueParticipants.filter(p => 
      p.name.toLowerCase().includes(searchTerm) ||
      p.email.toLowerCase().includes(searchTerm)
    ).slice(0, 10); // Limit to 10 suggestions
  };

  const updateStageScore = (rowId, stageName, value, field = null) => {
    setRows(rows.map(row => {
      if (row.id === rowId) {
        const currentStage = row.stages[stageName];
        
        // If field is specified, update that specific field within the object
        if (field) {
          return {
            ...row,
            stages: {
              ...row.stages,
              [stageName]: {
                ...currentStage,
                [field]: value
              }
            }
          };
        } else {
          // For simple formats (non-object), update the value directly
          return {
            ...row,
            stages: { ...row.stages, [stageName]: value }
          };
        }
      }
      return row;
    }));
  };

  const calculateStageScore = (value) => {
    if (!value) return 0;
    
    // For MATCH_POINTS, value is an object with points and time
    if (format?.stage_aggregation_method === 'MATCH_POINTS') {
      if (typeof value === 'object' && value.points && value.time) {
        const points = parseFloat(value.points);
        const time = parseFloat(value.time);
        if (isNaN(points) || isNaN(time) || time === 0) return 0;
        return points / time; // Return hit factor
      }
      return 0;
    }
    
    // For SCORING_RING or other formats with object structure, use the value field
    if (typeof value === 'object' && value.value !== undefined) {
      const numValue = parseFloat(value.value);
      if (isNaN(numValue)) return 0;
      return numValue;
    }
    
    // For simple formats, value is a number
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0;
    
    return numValue;
  };

  const calculateAggregateScore = (row) => {
    if (!format) return 0;
    
    // For MATCH_POINTS, aggregate score is calculated during ranking
    // based on all competitors' hit factors
    if (format.stage_aggregation_method === 'MATCH_POINTS') {
      return 0; // Will be calculated in rankedResults
    }
    
    const stageValues = stages.map(stage => {
      const value = row.stages[stage.stage_name];
      return calculateStageScore(value);
    });

    const aggregation = format.stage_aggregation_method;
    
    switch (aggregation) {
      case 'SUM':
        return stageValues.reduce((sum, val) => sum + val, 0);
      
      case 'AVERAGE':
        return stageValues.length > 0 
          ? stageValues.reduce((sum, val) => sum + val, 0) / stageValues.length 
          : 0;
      
      case 'WEIGHTED_SUM':
        return stages.reduce((sum, stage, idx) => {
          const weight = parseFloat(stage.weight) || 1;
          return sum + (stageValues[idx] * weight);
        }, 0);
      
      case 'BEST_OF':
        return Math.max(...stageValues, 0);
      
      case 'TOTAL_TIME':
      case 'LOWEST_TIME':
        return stageValues.reduce((sum, val) => sum + val, 0);
      
      default:
        return stageValues.reduce((sum, val) => sum + val, 0);
    }
  };

  const submitRow = async (row) => {
    // Validation
    if (!row.participant_name.trim()) {
      toast({
        title: 'Participant name required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!row.participant_email.trim()) {
      toast({
        title: 'Participant email required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Build stage scores
    const stageScoresArray = stages.map(stage => {
      const value = row.stages[stage.stage_name];
      let scoreData, calculatedValue;
      
      if (format.stage_aggregation_method === 'MATCH_POINTS') {
        // For MATCH_POINTS, store points and time, calculate hit factor
        const points = parseFloat(value?.points || 0);
        const time = parseFloat(value?.time || 0);
        scoreData = { points, time };
        calculatedValue = time > 0 ? points / time : 0; // Hit factor
      } else if (stage.scoring_primitive === 'SCORING_RING' && typeof value === 'object' && value.value !== undefined) {
        // For SCORING_RING with detailed scoring, store value plus x_count, inner_tens, clean_targets
        scoreData = {
          value: value.value || '0',
          x_count: value.x_count !== undefined && value.x_count !== '' ? parseInt(value.x_count) : null,
          inner_tens: value.inner_tens !== undefined && value.inner_tens !== '' ? parseInt(value.inner_tens) : null,
          clean_targets: value.clean_targets !== undefined && value.clean_targets !== '' ? parseInt(value.clean_targets) : null,
        };
        calculatedValue = parseFloat(value.value || 0);
      } else {
        // For other formats, store the value as-is
        scoreData = { value: value || '0' };
        calculatedValue = calculateStageScore(value);
      }
      
      return {
        stage_name: stage.stage_name,
        scoring_primitive: stage.scoring_primitive,
        score_data: scoreData,
        calculated_value: calculatedValue,
      };
    });

    const aggregateScore = calculateAggregateScore(row);

    // Calculate tiebreaker totals from stage scores (normally done by backend Lambda)
    // These are needed for proper ranking and display of staged results
    let total_x_count = 0;
    let total_inner_tens = 0;
    let total_clean_targets = 0;
    
    stageScoresArray.forEach(stageScore => {
      if (stageScore.score_data) {
        if (stageScore.score_data.x_count !== null && stageScore.score_data.x_count !== undefined) {
          total_x_count += stageScore.score_data.x_count;
        }
        if (stageScore.score_data.inner_tens !== null && stageScore.score_data.inner_tens !== undefined) {
          total_inner_tens += stageScore.score_data.inner_tens;
        }
        if (stageScore.score_data.clean_targets !== null && stageScore.score_data.clean_targets !== undefined) {
          total_clean_targets += stageScore.score_data.clean_targets;
        }
      }
    });

    // Create staged result (not saved to database yet)
    const stagedResult = {
      staged_id: `staged_${Date.now()}_${Math.random()}`, // Temporary ID
      participant_name: row.participant_name,
      participant_email: row.participant_email,
      division: row.division || null,
      category: row.category || null,
      stage_scores: stageScoresArray,
      aggregate_score: aggregateScore,
      total_x_count: total_x_count > 0 ? total_x_count : null,
      total_inner_tens: total_inner_tens > 0 ? total_inner_tens : null,
      total_clean_targets: total_clean_targets > 0 ? total_clean_targets : null,
      verified: false,
    };

    // Add to staged results
    setStagedResults([...stagedResults, stagedResult]);
    
    // Clear the row after staging
    const clearedRow = createEmptyRow();
    setRows(rows.map(r => r.id === row.id ? { ...clearedRow, id: row.id } : r));
    
    toast({
      title: 'Result staged',
      description: `Staged result for ${row.participant_name}. Click "Finalize Match" to save all results.`,
      status: 'info',
      duration: 2000,
    });
  };

  const handleSaveResults = async () => {
    if (stagedResults.length === 0) {
      toast({
        title: 'No staged results to save',
        description: 'All results have already been saved',
        status: 'info',
        duration: 3000,
      });
      return;
    }

    try {
      const resultsToSave = stagedResults.map(staged => {
        const result = {
          match_id: matchId,
          participant_name: staged.participant_name,
          participant_email: staged.participant_email,
          division: staged.division,
          category: staged.category,
          stage_scores: staged.stage_scores,
          aggregate_score: staged.aggregate_score,
          verified: staged.verified,
        };
        
        // Include tiebreaker_data if present
        if (staged.tiebreaker_data) {
          // Convert custom_fields from object to array format for GraphQL
          const tiebreakerData = { ...staged.tiebreaker_data };
          
          if (tiebreakerData.custom_fields && typeof tiebreakerData.custom_fields === 'object' && !Array.isArray(tiebreakerData.custom_fields)) {
            // Convert object {field: value} to array [{field, value}]
            tiebreakerData.custom_fields = Object.keys(tiebreakerData.custom_fields).map(field => ({
              field,
              value: tiebreakerData.custom_fields[field]
            }));
          }
          
          result.tiebreaker_data = tiebreakerData;
        }
        
        // Note: total_x_count, total_inner_tens, total_clean_targets are calculated by backend Lambda
        
        return result;
      });

      await bulkAddResults({
        variables: {
          match_id: matchId,
          results: resultsToSave,
        },
      });
    } catch (error) {
      console.error('Error saving results:', error);
    }
  };

  const handleStartMatch = async () => {
    if (!window.confirm(
      'Are you sure you want to start this match?\n\n' +
      'This will change the status to IN_PROGRESS and allow score entry.'
    )) {
      return;
    }

    try {
      const matchDetails = match.match_details;
      await updateMatchStatus({
        variables: {
          match_id: matchId,
          match_details: {
            match_name: matchDetails.match_name,
            match_category: matchDetails.match_category,
            match_discipline: matchDetails.match_discipline,
            match_date: matchDetails.match_date,
            match_format_id: matchDetails.match_format_id,
            match_status: 'IN_PROGRESS',
            match_location: matchDetails.match_location,
            match_director: matchDetails.match_director,
            match_director_email: matchDetails.match_director_email,
            registration_deadline: matchDetails.registration_deadline,
            max_participants: matchDetails.max_participants,
            match_description: matchDetails.match_description,
            weather_conditions: matchDetails.weather_conditions,
          }
        }
      });
    } catch (error) {
      console.error('Error starting match:', error);
    }
  };

  const handleReopenMatch = async () => {
    if (!window.confirm(
      'Are you sure you want to reopen this match?\n\n' +
      'This will change the status back to IN_PROGRESS and allow:\n' +
      '- Adding new results\n' +
      '- Editing existing results\n' +
      '- Deleting results\n\n' +
      'You can finalize the match again when edits are complete.'
    )) {
      return;
    }

    try {
      const matchDetails = match.match_details;
      await updateMatchStatus({
        variables: {
          match_id: matchId,
          match_details: {
            match_name: matchDetails.match_name,
            match_category: matchDetails.match_category,
            match_discipline: matchDetails.match_discipline,
            match_date: matchDetails.match_date,
            match_format_id: matchDetails.match_format_id,
            match_status: 'IN_PROGRESS',
            match_location: matchDetails.match_location,
            match_director: matchDetails.match_director,
            match_director_email: matchDetails.match_director_email,
            registration_deadline: matchDetails.registration_deadline,
            max_participants: matchDetails.max_participants,
            match_description: matchDetails.match_description,
            weather_conditions: matchDetails.weather_conditions,
          }
        }
      });
    } catch (error) {
      console.error('Error reopening match:', error);
    }
  };

  const handleFinalizeMatch = async () => {
    if (allResults.length === 0) {
      toast({
        title: 'Cannot finalize match',
        description: 'No results have been entered yet',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!window.confirm(
      `Are you sure you want to finalize this match?\n\n` +
      `This will:\n` +
      (stagedResults.length > 0 ? `- Save ${stagedResults.length} staged result(s) to the database\n` : '') +
      `- Mark the match as COMPLETED\n` +
      `- Open print dialog for match report\n` +
      `- Prevent further score entry\n\n` +
      `This action cannot be undone.`
    )) {
      return;
    }

    try {
      // First, save any staged results if any
      if (stagedResults.length > 0) {
        const resultsToSave = stagedResults.map(staged => {
          const result = {
            match_id: matchId,
            participant_name: staged.participant_name,
            participant_email: staged.participant_email,
            division: staged.division,
            category: staged.category,
            stage_scores: staged.stage_scores,
            aggregate_score: staged.aggregate_score,
            verified: staged.verified,
          };
          
          // Include tiebreaker_data if present
          if (staged.tiebreaker_data) {
            // Convert custom_fields from object to array format for GraphQL
            const tiebreakerData = { ...staged.tiebreaker_data };
            
            if (tiebreakerData.custom_fields && typeof tiebreakerData.custom_fields === 'object' && !Array.isArray(tiebreakerData.custom_fields)) {
              // Convert object {field: value} to array [{field, value}]
              tiebreakerData.custom_fields = Object.keys(tiebreakerData.custom_fields).map(field => ({
                field,
                value: tiebreakerData.custom_fields[field]
              }));
            }
            
            result.tiebreaker_data = tiebreakerData;
          }
          
          return result;
        });

        await bulkAddResults({
          variables: {
            match_id: matchId,
            results: resultsToSave,
          },
        });
      }

      // Then update match status to COMPLETED - only include allowed fields
      const matchDetails = match.match_details;
      await finalizeMatch({
        variables: {
          match_id: matchId,
          match_details: {
            match_name: matchDetails.match_name,
            match_category: matchDetails.match_category,
            match_discipline: matchDetails.match_discipline,
            match_date: matchDetails.match_date,
            match_format_id: matchDetails.match_format_id,
            match_status: 'COMPLETED',
            match_location: matchDetails.match_location,
            match_director: matchDetails.match_director,
            match_director_email: matchDetails.match_director_email,
            registration_deadline: matchDetails.registration_deadline,
            max_participants: matchDetails.max_participants,
            match_description: matchDetails.match_description,
            weather_conditions: matchDetails.weather_conditions,
          }
        }
      });

      // Trigger print dialog after a short delay to allow UI to update
      setTimeout(() => {
        window.print();
      }, 3500); // Wait for toast to disappear (3 second duration + 500ms buffer)
    } catch (error) {
      console.error('Error finalizing match:', error);
    }
  };

  const handleEditResult = (result) => {
    // Check if this is a staged result or saved result
    const isStaged = result.staged_id !== undefined;
    
    setEditingResult({ ...result, isStaged });
    
    if (isStaged) {
      // For staged results, use the result directly
      const formData = {
        participant_name: result.participant_name,
        participant_email: result.participant_email,
        division: result.division || '',
        category: result.category || '',
        stage_scores: {}
      };
      
      // Populate stage scores
      if (result.stage_scores && Array.isArray(result.stage_scores)) {
        result.stage_scores.forEach(score => {
          const scoreData = score.score_data || {};
          const stage = stages.find(s => s.stage_name === score.stage_name);
          
          if (format?.stage_aggregation_method === 'MATCH_POINTS') {
            formData.stage_scores[score.stage_name] = {
              points: scoreData.points !== undefined ? scoreData.points : '',
              time: scoreData.time !== undefined ? scoreData.time : ''
            };
          } else if (stage?.scoring_primitive === 'SCORING_RING' && (stage.parameters?.x_count || stage.parameters?.inner_tens || stage.parameters?.clean_targets)) {
            // For SCORING_RING with detailed scoring, load all fields
            formData.stage_scores[score.stage_name] = {
              value: scoreData.value !== undefined ? scoreData.value : '',
              x_count: scoreData.x_count !== undefined && scoreData.x_count !== null ? scoreData.x_count : '',
              inner_tens: scoreData.inner_tens !== undefined && scoreData.inner_tens !== null ? scoreData.inner_tens : '',
              clean_targets: scoreData.clean_targets !== undefined && scoreData.clean_targets !== null ? scoreData.clean_targets : '',
            };
          } else {
            formData.stage_scores[score.stage_name] = scoreData.value !== undefined ? scoreData.value : '';
          }
        });
      }
      
      console.log('Edit staged result - formData:', formData);
      setEditFormData(formData);
    } else {
      // For saved results, use result_details
      const details = result.result_details;
      const formData = {
        participant_name: details.participant_name,
        participant_email: details.participant_email,
        division: details.division || '',
        category: details.category || '',
        stage_scores: {}
      };
      
      // Populate stage scores
      if (details.stage_scores && Array.isArray(details.stage_scores)) {
        details.stage_scores.forEach(score => {
          const scoreData = score.score_data || {};
          const stage = stages.find(s => s.stage_name === score.stage_name);
          
          if (format?.stage_aggregation_method === 'MATCH_POINTS') {
            formData.stage_scores[score.stage_name] = {
              points: scoreData.points !== undefined ? scoreData.points : '',
              time: scoreData.time !== undefined ? scoreData.time : ''
            };
          } else if (stage?.scoring_primitive === 'SCORING_RING' && (stage.parameters?.x_count || stage.parameters?.inner_tens || stage.parameters?.clean_targets)) {
            // For SCORING_RING with detailed scoring, load all fields
            formData.stage_scores[score.stage_name] = {
              value: scoreData.value !== undefined ? scoreData.value : '',
              x_count: scoreData.x_count !== undefined && scoreData.x_count !== null ? scoreData.x_count : '',
              inner_tens: scoreData.inner_tens !== undefined && scoreData.inner_tens !== null ? scoreData.inner_tens : '',
              clean_targets: scoreData.clean_targets !== undefined && scoreData.clean_targets !== null ? scoreData.clean_targets : '',
            };
          } else {
            formData.stage_scores[score.stage_name] = scoreData.value !== undefined ? scoreData.value : '';
          }
        });
      }
      
      console.log('Edit saved result - formData:', formData);
      console.log('Edit saved result - stages:', stages);
      setEditFormData(formData);
    }
  };

  const handleSaveEdit = () => {
    if (!editingResult || !editFormData) return;

    // Build stage scores array
    const stageScoresArray = stages.map(stage => {
      const value = editFormData.stage_scores[stage.stage_name];
      let scoreData, calculatedValue;
      
      if (format?.stage_aggregation_method === 'MATCH_POINTS') {
        // For MATCH_POINTS, store points and time, calculate hit factor
        const points = parseFloat(value?.points || 0);
        const time = parseFloat(value?.time || 0);
        scoreData = { points, time };
        calculatedValue = time > 0 ? points / time : 0; // Hit factor
      } else if (stage.scoring_primitive === 'SCORING_RING' && typeof value === 'object' && value.value !== undefined) {
        // For SCORING_RING with detailed scoring
        scoreData = {
          value: value.value || '0',
          x_count: value.x_count !== undefined && value.x_count !== '' ? parseInt(value.x_count) : null,
          inner_tens: value.inner_tens !== undefined && value.inner_tens !== '' ? parseInt(value.inner_tens) : null,
          clean_targets: value.clean_targets !== undefined && value.clean_targets !== '' ? parseInt(value.clean_targets) : null,
        };
        calculatedValue = parseFloat(value.value || 0);
      } else {
        // For other formats, store the value as-is
        scoreData = { value: value || '0' };
        calculatedValue = calculateStageScore(value);
      }
      
      return {
        stage_name: stage.stage_name,
        scoring_primitive: stage.scoring_primitive,
        score_data: scoreData,
        calculated_value: calculatedValue,
      };
    });

    // Calculate aggregate score
    const stageValues = stages.map(stage => {
      const value = editFormData.stage_scores[stage.stage_name];
      return calculateStageScore(value);
    });

    let aggregateScore = 0;
    const aggregation = format.stage_aggregation_method;
    
    // For MATCH_POINTS, aggregate score is calculated during ranking
    if (aggregation === 'MATCH_POINTS') {
      aggregateScore = 0; // Will be calculated in rankedResults
    } else {
      switch (aggregation) {
        case 'SUM':
          aggregateScore = stageValues.reduce((sum, val) => sum + val, 0);
          break;
        case 'AVERAGE':
          aggregateScore = stageValues.length > 0 
            ? stageValues.reduce((sum, val) => sum + val, 0) / stageValues.length 
            : 0;
          break;
        case 'WEIGHTED_SUM':
          aggregateScore = stages.reduce((sum, stage, idx) => {
            const weight = parseFloat(stage.weight) || 1;
            return sum + (stageValues[idx] * weight);
          }, 0);
          break;
        case 'BEST_OF':
          aggregateScore = Math.max(...stageValues, 0);
          break;
        case 'TOTAL_TIME':
        case 'LOWEST_TIME':
          aggregateScore = stageValues.reduce((sum, val) => sum + val, 0);
          break;
        default:
          aggregateScore = stageValues.reduce((sum, val) => sum + val, 0);
      }
    }

    // Note: total_x_count, total_inner_tens, total_clean_targets are calculated by backend Lambda

    if (editingResult.isStaged) {
      // Update staged result
      const updatedStaged = stagedResults.map(r => 
        r.staged_id === editingResult.staged_id
          ? {
              ...r,
              participant_name: editFormData.participant_name,
              participant_email: editFormData.participant_email,
              division: editFormData.division || null,
              category: editFormData.category || null,
              stage_scores: stageScoresArray,
              aggregate_score: aggregateScore,
            }
          : r
      );
      setStagedResults(updatedStaged);
      
      toast({
        title: 'Staged result updated',
        status: 'success',
        duration: 2000,
      });
      handleCloseEditModal();
    } else {
      // Update saved result via mutation
      // Convert custom_fields from array to object for GraphQL input
      const existingTiebreakerData = editingResult.result_details.tiebreaker_data;
      let tiebreakerDataInput = null;
      
      if (existingTiebreakerData) {
        // custom_fields is already in array format from GraphQL
        tiebreakerDataInput = {
          shootoff_1_score: existingTiebreakerData.shootoff_1_score || null,
          shootoff_2_score: existingTiebreakerData.shootoff_2_score || null,
          shootoff_3_score: existingTiebreakerData.shootoff_3_score || null,
          custom_fields: existingTiebreakerData.custom_fields || null,
        };
      }
      
      updateResult({
        variables: {
          result_id: editingResult.result_id,
          result_details: {
            match_id: matchId,
            participant_name: editFormData.participant_name,
            participant_email: editFormData.participant_email,
            division: editFormData.division || null,
            category: editFormData.category || null,
            stage_scores: stageScoresArray,
            aggregate_score: aggregateScore,
            verified: editingResult.result_details.verified || false,
            tiebreaker_data: tiebreakerDataInput,
          },
        },
      });
    }
  };

  const handleCloseEditModal = () => {
    setEditingResult(null);
    setEditFormData(null);
  };

  const handleOpenTiebreakerModal = (result) => {
    const isStaged = result.staged_id !== undefined;
    const details = result.result_details || result;
    
    setTiebreakerModalResult({ ...result, isStaged });
    
    // Initialize form data with existing tiebreaker data
    const formData = {
      shootoffs: {},
      custom_fields: {}
    };
    
    // Load existing shootoff data
    if (format?.tiebreaker_rules?.enabled && format.tiebreaker_rules.steps) {
      format.tiebreaker_rules.steps.forEach(step => {
        if (step.type === 'SHOOTOFF') {
          const shootoffField = `shootoff_${step.order}_score`;
          formData.shootoffs[step.order] = details.tiebreaker_data?.[shootoffField] || '';
        } else if (step.type === 'CUSTOM_FIELD') {
          // custom_fields can be either array or object depending on source
          const customFields = details.tiebreaker_data?.custom_fields;
          let fieldValue = '';
          
          if (Array.isArray(customFields)) {
            // Array format from GraphQL: [{field: "name", value: 10}, ...]
            const field = customFields.find(f => f.field === step.field);
            fieldValue = field?.value || '';
          } else if (customFields && typeof customFields === 'object') {
            // Object format from staged results: {field_name: value, ...}
            fieldValue = customFields[step.field] || '';
          }
          
          formData.custom_fields[step.field] = fieldValue;
        }
      });
    }
    
    setTiebreakerFormData(formData);
  };

  const handleCloseTiebreakerModal = () => {
    setTiebreakerModalResult(null);
    setTiebreakerFormData(null);
  };

  const handleSaveTiebreakerData = () => {
    if (!tiebreakerModalResult || !tiebreakerFormData) return;

    // Build tiebreaker_data object for GraphQL input
    const tiebreakerDataInput = {};
    
    // Add shootoff scores - ONLY send if they have values
    // Initialize all three to null first
    tiebreakerDataInput.shootoff_1_score = null;
    tiebreakerDataInput.shootoff_2_score = null;
    tiebreakerDataInput.shootoff_3_score = null;
    
    // Then override with actual values if present
    Object.keys(tiebreakerFormData.shootoffs).forEach(order => {
      const orderNum = parseInt(order);
      if (orderNum >= 1 && orderNum <= 3) {
        const value = tiebreakerFormData.shootoffs[order];
        if (value !== '' && value !== null && value !== undefined) {
          tiebreakerDataInput[`shootoff_${order}_score`] = parseInt(value);
        }
      }
    });
    
    // Add custom fields as array of {field, value} pairs for proper typing
    const customFieldsArray = [];
    Object.keys(tiebreakerFormData.custom_fields).forEach(field => {
      const value = tiebreakerFormData.custom_fields[field];
      if (value !== '' && value !== null && value !== undefined) {
        customFieldsArray.push({
          field,
          value: parseFloat(value)
        });
      }
    });
    
    // Always include custom_fields, even if empty (send null if no fields)
    tiebreakerDataInput.custom_fields = customFieldsArray.length > 0 ? customFieldsArray : null;

    if (tiebreakerModalResult.isStaged) {
      // Update staged result - convert array back to object for local use
      const customFieldsObj = {};
      customFieldsArray.forEach(({ field, value }) => {
        customFieldsObj[field] = value;
      });
      
      const tiebreakerDataLocal = {
        ...tiebreakerDataInput,
        custom_fields: customFieldsObj // Store as object for easier access in UI
      };
      
      const updatedStaged = stagedResults.map(r => 
        r.staged_id === tiebreakerModalResult.staged_id
          ? {
              ...r,
              tiebreaker_data: tiebreakerDataLocal
            }
          : r
      );
      setStagedResults(updatedStaged);
      
      toast({
        title: 'Tiebreaker data updated',
        description: 'Staged result updated with tiebreaker data',
        status: 'success',
        duration: 2000,
      });
      handleCloseTiebreakerModal();
    } else {
      // Update saved result via mutation
      // CRITICAL: Clean stage_scores to remove __typename and other Apollo metadata
      const details = tiebreakerModalResult.result_details;
      
      // Clean stage_scores for input - remove __typename fields
      const cleanedStageScores = details.stage_scores.map(stage => ({
        stage_name: stage.stage_name,
        scoring_primitive: stage.scoring_primitive,
        calculated_value: stage.calculated_value,
        score_data: {
          value: stage.score_data?.value || null,
          points: stage.score_data?.points || null,
          time: stage.score_data?.time || null,
          x_count: stage.score_data?.x_count || null,
          inner_tens: stage.score_data?.inner_tens || null,
          clean_targets: stage.score_data?.clean_targets || null,
        },
      }));
      
      updateResult({
        variables: {
          result_id: tiebreakerModalResult.result_id,
          result_details: {
            match_id: matchId,
            participant_name: details.participant_name,
            participant_email: details.participant_email,
            division: details.division || null,
            category: details.category || null,
            stage_scores: cleanedStageScores,
            aggregate_score: details.aggregate_score,
            verified: details.verified || false,
            tiebreaker_data: tiebreakerDataInput,
          },
        },
      });
      handleCloseTiebreakerModal();
    }
  };

  const handleDeleteResult = (result) => {
    const isStaged = result.staged_id !== undefined;
    
    if (isStaged) {
      // Delete staged result
      if (window.confirm('Are you sure you want to remove this staged result?')) {
        setStagedResults(stagedResults.filter(r => r.staged_id !== result.staged_id));
        toast({
          title: 'Staged result removed',
          status: 'success',
          duration: 2000,
        });
      }
    } else {
      // Delete saved result via mutation
      if (window.confirm('Are you sure you want to delete this result? This action cannot be undone.')) {
        deleteResult({
          variables: { result_id: result.result_id },
        });
      }
    }
  };

  if (matchLoading || resultsLoading) {
    return (
      <Box bg="#F7F7F7" minH="100vh">
        <WithSubnavigation />
        <Box pt="84px" px={{ base: '42px', md: '84px' }}>
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" />
          </Flex>
        </Box>
        <Box className="no-print">
          <Footer />
        </Box>
      </Box>
    );
  }

  if (!match) {
    return (
      <Box bg="#F7F7F7" minH="100vh">
        <WithSubnavigation />
        <Box pt="84px" px={{ base: '42px', md: '84px' }}>
          <Text>Match not found</Text>
        </Box>
        <Box className="no-print">
          <Footer />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          /* Hide navigation, buttons, and UI elements */
          nav, button, .chakra-button, .no-print {
            display: none !important;
          }
          
          /* Hide header navigation */
          header {
            display: none !important;
          }
          
          /* Hide footer */
          footer {
            display: none !important;
          }
          
          /* Hide toast notifications */
          .chakra-toast, .chakra-toast__inner {
            display: none !important;
          }
          
          /* Remove top padding from main content area */
          body > div > div {
            padding-top: 0 !important;
          }
          
          /* Show print-only elements */
          .print-only {
            display: block !important;
          }
          
          /* Hide the screen header (Results Entry - Status) */
          .chakra-flex:has(.chakra-badge) {
            display: none !important;
          }
          
          /* Page break before results section */
          .results-section-container {
            page-break-before: always;
          }
          
          /* Reset page margins and padding */
          @page {
            margin: 0.5in;
            size: letter portrait;
          }
          
          body {
            margin: 0;
            padding: 0;
            font-size: 10pt;
          }
          
          /* Make tables print-friendly */
          table {
            page-break-inside: auto;
            border-collapse: collapse;
            width: 100%;
            font-size: 9pt;
          }
          
          /* Avoid page breaks inside table rows */
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group;
          }
          
          /* Print in black and white for better readability */
          * {
            color: black !important;
            background: white !important;
          }
          
          /* Keep borders visible */
          table, th, td {
            border: 1px solid black !important;
            padding: 4px 8px !important;
          }
          
          th {
            font-weight: bold !important;
            background-color: #f0f0f0 !important;
          }
          
          /* Adjust heading sizes for print */
          h1 {
            font-size: 18pt;
            margin-bottom: 12pt;
            page-break-after: avoid;
          }
          
          h2 {
            font-size: 14pt;
            margin-top: 12pt;
            margin-bottom: 8pt;
            page-break-after: avoid;
          }
          
          h3 {
            font-size: 12pt;
            margin-top: 8pt;
            margin-bottom: 6pt;
            page-break-after: avoid;
          }
          
          /* Remove box shadows and unnecessary styling */
          .chakra-box {
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          /* Hide badges in print */
          .chakra-badge {
            display: none !important;
          }
          
          /* Compact spacing for print */
          .chakra-stack > * {
            margin-top: 4pt !important;
            margin-bottom: 4pt !important;
          }
          
          /* Make match details boxes more compact */
          .print-only .chakra-box {
            border: 1px solid black !important;
            padding: 8pt !important;
            margin-bottom: 8pt !important;
          }
          
          /* Ensure division/category sections stay together */
          .results-section {
            page-break-inside: avoid;
            margin-bottom: 12pt;
          }
          
          /* Compact text */
          p, .chakra-text {
            margin: 2pt 0 !important;
            line-height: 1.3 !important;
          }
        }
      `}</style>
      <Box bg="#F7F7F7" minH="100vh">
        <WithSubnavigation />
        <Box pt="84px" px={{ base: '42px', md: '84px' }} pb={8}>
        <VStack spacing={6} align="stretch">
          {/* Print-Only Match Report Header */}
          <Box className="print-only" display="none" mb={6}>
            <Heading size="xl" textAlign="center" mb={4}>
              {match.match_details.match_name}
            </Heading>
            <Box borderWidth="1px" borderColor="black" p={4} mb={4}>
              <Heading size="md" mb={3}>Match Details</Heading>
              <VStack align="stretch" spacing={2}>
                <Text><strong>Date:</strong> {new Date(match.match_details.match_date * 1000).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</Text>
                <Text><strong>Location:</strong> {match.match_details.match_location || 'Golden Gun Club'}</Text>
                <Text><strong>Match Director:</strong> {match.match_details.match_director}</Text>
                {match.match_details.match_description && (
                  <Text><strong>Description:</strong> {match.match_details.match_description}</Text>
                )}
                <Text><strong>Total Participants:</strong> {allResults.length}</Text>
              </VStack>
            </Box>
            {format && (
              <Box borderWidth="1px" borderColor="black" p={4} mb={4}>
                <Heading size="md" mb={3}>Match Format: {format.format_name}</Heading>
                <VStack align="stretch" spacing={2}>
                  <Text><strong>Category:</strong> {format.match_category || 'N/A'}</Text>
                  <Text><strong>Discipline:</strong> {format.match_discipline || 'N/A'}</Text>
                  <Text><strong>Scoring Method:</strong> {format.stage_aggregation_method}</Text>
                  {format.divisions && format.divisions.length > 0 && (
                    <Text><strong>Divisions:</strong> {format.divisions.join(', ')}</Text>
                  )}
                  {format.categories && format.categories.length > 0 && (
                    <Text><strong>Categories:</strong> {format.categories.join(', ')}</Text>
                  )}
                  {stages.length > 0 && (
                    <Box mt={2}>
                      <Text fontWeight="bold">Stages:</Text>
                      {stages.map((stage, idx) => (
                        <Text key={idx} ml={4}>
                          {idx + 1}. {stage.stage_name} ({stage.scoring_primitive})
                        </Text>
                      ))}
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
          </Box>

          {/* Header */}
          <Flex justify="space-between" align="center" className="no-print">
            <Box>
              <Heading size="lg">{match.match_details.match_name}</Heading>
              <Text color="gray.600" mt={1}>
                Results Entry - Status: <Badge colorScheme={
                  isCompleted ? 'gray' : isInProgress ? 'green' : 'blue'
                }>
                  {match.match_details.match_status}
                </Badge>
              </Text>
              {stagedResults.length > 0 && (
                <Text color="orange.600" mt={1} fontWeight="medium">
                  {stagedResults.length} result{stagedResults.length !== 1 ? 's' : ''} staged (not yet saved)
                </Text>
              )}
            </Box>
            <HStack>
              <Button onClick={() => navigate('/matches/manage')}>
                Back to Matches
              </Button>
              {isUpcoming && (
                <Button 
                  colorScheme="green" 
                  onClick={handleStartMatch}
                >
                  Start Match
                </Button>
              )}
              {isInProgress && stagedResults.length > 0 && (
                <Button 
                  colorScheme="blue" 
                  onClick={handleSaveResults}
                  isLoading={bulkAdding}
                >
                  Save Results
                </Button>
              )}
              {isInProgress && allResults.length > 0 && (
                <Button 
                  colorScheme="green" 
                  onClick={handleFinalizeMatch}
                  isLoading={bulkAdding || finalizing}
                >
                  Finalize Match
                </Button>
              )}
              {isCompleted && (
                <>
                  <Button 
                    colorScheme="orange" 
                    onClick={handleReopenMatch}
                  >
                    Reopen Match
                  </Button>
                  <Button colorScheme="blue" onClick={() => window.print()}>
                    Print Report
                  </Button>
                </>
              )}
            </HStack>
          </Flex>

          {/* Entry Form */}
          {canEnterScores ? (
            <>
              {allResults.length === 0 && (
                <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200" className="no-print">
                  <Text color="blue.800">
                    Enter results below. Results are staged locally until you click "Save Results" to save them to the database, or "Finalize Match" to save all results and mark the match as COMPLETED.
                  </Text>
                </Box>
              )}
              {stagedResults.length > 0 && (
                <Box bg="orange.50" p={4} borderRadius="md" border="1px solid" borderColor="orange.200" className="no-print">
                  <Text color="orange.800" fontWeight="medium">
                    You have {stagedResults.length} staged result{stagedResults.length !== 1 ? 's' : ''} that haven't been saved yet. Click "Save Results" to save them without finalizing, or "Finalize Match" to save and complete the match.
                  </Text>
                </Box>
              )}
              <Box bg="white" p={6} borderRadius="md" shadow="sm" className="no-print">
              <Heading size="md" mb={4}>Add Results</Heading>
              
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th width="200px">Name</Th>
                      <Th width="200px">Email</Th>
                      <Th width="120px">Division</Th>
                      <Th width="120px">Category</Th>
                      {stages.map(stage => (
                        <Th key={stage.stage_name} width={format?.stage_aggregation_method === 'MATCH_POINTS' ? '150px' : '100px'}>
                          {stage.stage_name}
                          {format?.stage_aggregation_method === 'MATCH_POINTS' && (
                            <Text fontSize="xs" fontWeight="normal" color="gray.600">
                              Pts / Time
                            </Text>
                          )}
                        </Th>
                      ))}
                      <Th width="100px">Total</Th>
                      <Th width="100px">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map(row => {
                      const suggestions = focusedRowId === row.id && showSuggestions 
                        ? getFilteredSuggestions(row.id) 
                        : [];
                      
                      return (
                      <Tr key={row.id}>
                        <Td>
                          <Popover
                            isOpen={focusedRowId === row.id && showSuggestions && suggestions.length > 0}
                            placement="bottom-start"
                            closeOnBlur={true}
                          >
                            <PopoverTrigger>
                              <Input
                                size="sm"
                                value={row.participant_name}
                                onChange={(e) => updateRow(row.id, 'participant_name', e.target.value)}
                                onBlur={() => {
                                  // Delay to allow click on suggestion
                                  setTimeout(() => {
                                    setShowSuggestions(false);
                                    setFocusedRowId(null);
                                  }, 200);
                                }}
                                placeholder="Name"
                                autoComplete="off"
                              />
                            </PopoverTrigger>
                            <PopoverContent width="300px">
                              <PopoverBody p={0}>
                                <List spacing={0}>
                                  {suggestions.map((participant, idx) => (
                                    <ListItem
                                      key={idx}
                                      px={3}
                                      py={2}
                                      cursor="pointer"
                                      _hover={{ bg: 'gray.100' }}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        selectParticipant(row.id, participant);
                                      }}
                                    >
                                      <Text fontWeight="medium" fontSize="sm">
                                        {participant.name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600">
                                        {participant.email}
                                      </Text>
                                    </ListItem>
                                  ))}
                                </List>
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            type="email"
                            value={row.participant_email}
                            onChange={(e) => updateRow(row.id, 'participant_email', e.target.value)}
                            placeholder="Email"
                          />
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            value={row.division}
                            onChange={(e) => updateRow(row.id, 'division', e.target.value)}
                            placeholder="Select division"
                          >
                            {format?.divisions?.map((division) => (
                              <option key={division} value={division}>
                                {division}
                              </option>
                            ))}
                          </Select>
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            value={row.category}
                            onChange={(e) => updateRow(row.id, 'category', e.target.value)}
                            placeholder="Select category"
                          >
                            {format?.categories?.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </Select>
                        </Td>
                        {stages.map(stage => (
                          <Td key={stage.stage_name}>
                            {format?.stage_aggregation_method === 'MATCH_POINTS' ? (
                              <HStack spacing={1}>
                                <Input
                                  size="sm"
                                  type="number"
                                  step="1"
                                  value={row.stages[stage.stage_name]?.points || ''}
                                  onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value, 'points')}
                                  placeholder="Pts"
                                  width="60px"
                                />
                                <Text fontSize="xs">/</Text>
                                <Input
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  value={row.stages[stage.stage_name]?.time || ''}
                                  onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value, 'time')}
                                  placeholder="Time"
                                  width="60px"
                                />
                              </HStack>
                            ) : stage.scoring_primitive === 'SCORING_RING' && (stage.parameters?.x_count || stage.parameters?.inner_tens || stage.parameters?.clean_targets) ? (
                              <VStack spacing={1} align="stretch">
                                <Input
                                  size="sm"
                                  type="number"
                                  step="1"
                                  value={row.stages[stage.stage_name]?.value || ''}
                                  onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value, 'value')}
                                  placeholder="Score"
                                />
                                <HStack spacing={1}>
                                  {stage.parameters?.x_count && (
                                    <Input
                                      size="sm"
                                      type="number"
                                      step="1"
                                      value={row.stages[stage.stage_name]?.x_count || ''}
                                      onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value, 'x_count')}
                                      placeholder="X"
                                      width="50px"
                                      title="X Count"
                                    />
                                  )}
                                  {stage.parameters?.inner_tens && (
                                    <Input
                                      size="sm"
                                      type="number"
                                      step="1"
                                      value={row.stages[stage.stage_name]?.inner_tens || ''}
                                      onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value, 'inner_tens')}
                                      placeholder="10"
                                      width="50px"
                                      title="Inner 10s"
                                    />
                                  )}
                                  {stage.parameters?.clean_targets && (
                                    <Input
                                      size="sm"
                                      type="number"
                                      step="1"
                                      value={row.stages[stage.stage_name]?.clean_targets || ''}
                                      onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value, 'clean_targets')}
                                      placeholder="C"
                                      width="50px"
                                      title="Clean Targets"
                                    />
                                  )}
                                </HStack>
                              </VStack>
                            ) : (
                              <Input
                                size="sm"
                                type="number"
                                step="0.01"
                                value={row.stages[stage.stage_name]}
                                onChange={(e) => updateStageScore(row.id, stage.stage_name, e.target.value)}
                                placeholder="0"
                              />
                            )}
                          </Td>
                        ))}
                        <Td>
                          {format?.stage_aggregation_method === 'MATCH_POINTS' ? (
                            <Text fontSize="xs" color="gray.500">
                              Calculated
                            </Text>
                          ) : (
                            <Text fontWeight="bold" fontSize="sm">
                              {calculateAggregateScore(row).toFixed(2)}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <IconButton
                              size="sm"
                              icon={<CheckIcon />}
                              colorScheme="green"
                              onClick={() => submitRow(row)}
                              isLoading={adding}
                              aria-label="Submit"
                            />
                            {rows.length > 1 && (
                              <IconButton
                                size="sm"
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => removeRow(row.id)}
                                aria-label="Remove"
                              />
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    );
                    })}
                  </Tbody>
                </Table>
              </Box>

              <Button
                leftIcon={<AddIcon />}
                size="sm"
                mt={4}
                onClick={addRow}
                variant="outline"
              >
                Add Another Row
              </Button>
            </Box>
            </>
          ) : isUpcoming ? (
            <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200" className="no-print">
              <Text fontWeight="bold" color="blue.800">
                This match has not been started yet. Click "Start Match" to begin entering results.
              </Text>
            </Box>
          ) : (
            <Box bg="yellow.50" p={4} borderRadius="md" border="1px solid" borderColor="yellow.200" className="no-print">
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" color="yellow.800">
                  This match is completed. Score entry is disabled. Individual results can still be edited or deleted below.
                </Text>
                <Text color="yellow.800" fontSize="sm">
                  If you need to make an alteration to the match scores, please click on "Reopen Match", make any changes, and then "Finalize Match" again to correct score reports.
                </Text>
              </VStack>
            </Box>
          )}

          {/* Existing Results */}
          <Box bg="white" p={6} borderRadius="md" shadow="sm" className="results-section-container">
            <Heading size="md" mb={4}>
              Current Results ({allResults.length} total
              {savedResults.length > 0 && ` - ${savedResults.length} saved`}
              {stagedResults.length > 0 && `, ${stagedResults.length} staged`})
            </Heading>
            
            {allResults.length === 0 ? (
              <Text color="gray.500">No results entered yet</Text>
            ) : (
              <VStack spacing={6} align="stretch">
                {Object.keys(resultsByDivisionCategory).map(key => {
                  const [division, category] = key.split('|');
                  const groupResults = resultsByDivisionCategory[key];
                  
                  return (
                    <Box key={key} className="results-section">
                      <Heading size="sm" mb={3} color="blue.600">
                        {division} - {category} ({groupResults.length} {groupResults.length === 1 ? 'participant' : 'participants'})
                      </Heading>
                      <Box overflowX="auto">
                        <Table size="sm" variant="striped">
                          <Thead>
                            <Tr>
                              <Th>Rank</Th>
                              <Th>Participant</Th>
                              {format?.stage_aggregation_method === 'MATCH_POINTS' && stages.map(stage => (
                                <Th key={stage.stage_name} isNumeric>
                                  {stage.stage_name}
                                  <Text fontSize="xs" fontWeight="normal" color="gray.600">
                                    HF / Pts
                                  </Text>
                                </Th>
                              ))}
                              <Th isNumeric>
                                {format?.stage_aggregation_method === 'MATCH_POINTS' ? 'Total Match Points' : 'Score'}
                              </Th>
                              {format?.stage_aggregation_method !== 'MATCH_POINTS' && <Th>Tie-Breaker</Th>}
                              {!isUpcoming && <Th>Actions</Th>}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {groupResults.map((result) => {
                              const isStaged = result.staged_id !== undefined;
                              const details = result.result_details || result;
                              
                              return (
                              <Tr key={result.result_id || result.staged_id} bg={isStaged ? 'orange.50' : 'white'}>
                                <Td fontWeight="bold">{result.rank}</Td>
                                <Td>
                                  {details.participant_name}
                                  {isStaged && (
                                    <Badge ml={2} colorScheme="orange" fontSize="xs">
                                      Staged
                                    </Badge>
                                  )}
                                </Td>
                                {format?.stage_aggregation_method === 'MATCH_POINTS' && details.match_points_breakdown?.map((stageData, idx) => (
                                  <Td key={idx} isNumeric>
                                    <VStack spacing={0} align="end">
                                      <Text fontSize="sm" fontWeight={stageData.is_winner ? 'bold' : 'normal'}>
                                        {stageData.hit_factor.toFixed(2)}
                                        {stageData.is_winner && ' 🏆'}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600">
                                        {stageData.match_points.toFixed(1)} pts
                                      </Text>
                                    </VStack>
                                  </Td>
                                ))}
                                <Td isNumeric fontWeight="bold">
                                  {details.aggregate_score.toFixed(2)}
                                  {format?.stage_aggregation_method === 'MATCH_POINTS' && (
                                    <Text fontSize="xs" color="gray.600" fontWeight="normal">
                                      pts
                                    </Text>
                                  )}
                                </Td>
                                {format?.stage_aggregation_method !== 'MATCH_POINTS' && (
                                  <Td>
                                    {details.rank_note ? (
                                      <Text fontSize="sm" color="gray.600">
                                        {details.rank_note}
                                      </Text>
                                    ) : (
                                      <TiebreakerDisplay 
                                        format={format} 
                                        details={details} 
                                      />
                                    )}
                                  </Td>
                                )}
                                {!isUpcoming && (
                                  <Td>
                                    <HStack spacing={1}>
                                      {format?.tiebreaker_rules?.enabled && format.tiebreaker_rules.steps?.length > 0 && (
                                        <IconButton
                                          size="sm"
                                          icon={<Text fontSize="xs">🎯</Text>}
                                          colorScheme="purple"
                                          variant="ghost"
                                          onClick={() => handleOpenTiebreakerModal(result)}
                                          aria-label="Tiebreaker Data"
                                          title="View/Enter tiebreaker data"
                                        />
                                      )}
                                      <IconButton
                                        size="sm"
                                        icon={<EditIcon />}
                                        colorScheme="blue"
                                        variant="ghost"
                                        onClick={() => handleEditResult(result)}
                                        aria-label="Edit"
                                        title="Edit result"
                                      />
                                      <IconButton
                                        size="sm"
                                        icon={<DeleteIcon />}
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => handleDeleteResult(result)}
                                        aria-label="Delete"
                                        title={isStaged ? "Remove staged result" : "Delete result"}
                                      />
                                    </HStack>
                                  </Td>
                                )}
                              </Tr>
                            );
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </VStack>
      </Box>
      <Box className="no-print">
        <Footer />
      </Box>
    </Box>

    {/* Edit Result Modal */}
    <Modal isOpen={!!editingResult} onClose={handleCloseEditModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Result</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editFormData && (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Participant Name</FormLabel>
                  <Input
                    value={editFormData.participant_name}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      participant_name: e.target.value
                    })}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Participant Email</FormLabel>
                  <Input
                    type="email"
                    value={editFormData.participant_email}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      participant_email: e.target.value
                    })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Division</FormLabel>
                  <Select
                    value={editFormData.division}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      division: e.target.value
                    })}
                    placeholder="Select division"
                  >
                    {/* Show current value even if not in format (backward compatibility) */}
                    {editFormData.division && !format?.divisions?.includes(editFormData.division) && (
                      <option value={editFormData.division}>
                        {editFormData.division} (legacy)
                      </option>
                    )}
                    {format?.divisions?.map((division) => (
                      <option key={division} value={division}>
                        {division}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Category</FormLabel>
                  <Select
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      category: e.target.value
                    })}
                    placeholder="Select category"
                  >
                    {/* Show current value even if not in format (backward compatibility) */}
                    {editFormData.category && !format?.categories?.includes(editFormData.category) && (
                      <option value={editFormData.category}>
                        {editFormData.category} (legacy)
                      </option>
                    )}
                    {format?.categories?.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <Heading size="sm" mt={4}>Stage Scores</Heading>
                {stages && stages.length > 0 ? (
                  stages.map(stage => (
                    <FormControl key={stage.stage_name}>
                      <FormLabel>{stage.stage_name}</FormLabel>
                      {format?.stage_aggregation_method === 'MATCH_POINTS' ? (
                        <HStack>
                          <Box flex={1}>
                            <FormLabel fontSize="xs">Points</FormLabel>
                            <Input
                              type="number"
                              step="1"
                              value={editFormData.stage_scores[stage.stage_name]?.points || ''}
                              onChange={(e) => setEditFormData({
                                ...editFormData,
                                stage_scores: {
                                  ...editFormData.stage_scores,
                                  [stage.stage_name]: {
                                    ...editFormData.stage_scores[stage.stage_name],
                                    points: e.target.value
                                  }
                                }
                              })}
                              placeholder="Points"
                            />
                          </Box>
                          <Box flex={1}>
                            <FormLabel fontSize="xs">Time (seconds)</FormLabel>
                            <Input
                              type="number"
                              step="0.01"
                              value={editFormData.stage_scores[stage.stage_name]?.time || ''}
                              onChange={(e) => setEditFormData({
                                ...editFormData,
                                stage_scores: {
                                  ...editFormData.stage_scores,
                                  [stage.stage_name]: {
                                    ...editFormData.stage_scores[stage.stage_name],
                                    time: e.target.value
                                  }
                                }
                              })}
                              placeholder="Time"
                            />
                          </Box>
                        </HStack>
                      ) : stage.scoring_primitive === 'SCORING_RING' && (stage.parameters?.x_count || stage.parameters?.inner_tens || stage.parameters?.clean_targets) ? (
                        <VStack align="stretch" spacing={2}>
                          <Box>
                            <FormLabel fontSize="xs">Score</FormLabel>
                            <Input
                              type="number"
                              step="1"
                              value={editFormData.stage_scores[stage.stage_name]?.value || ''}
                              onChange={(e) => setEditFormData({
                                ...editFormData,
                                stage_scores: {
                                  ...editFormData.stage_scores,
                                  [stage.stage_name]: {
                                    ...editFormData.stage_scores[stage.stage_name],
                                    value: e.target.value
                                  }
                                }
                              })}
                              placeholder="Score"
                            />
                          </Box>
                          <HStack>
                            {stage.parameters?.x_count && (
                              <Box flex={1}>
                                <FormLabel fontSize="xs">X Count</FormLabel>
                                <Input
                                  type="number"
                                  step="1"
                                  value={editFormData.stage_scores[stage.stage_name]?.x_count || ''}
                                  onChange={(e) => setEditFormData({
                                    ...editFormData,
                                    stage_scores: {
                                      ...editFormData.stage_scores,
                                      [stage.stage_name]: {
                                        ...editFormData.stage_scores[stage.stage_name],
                                        x_count: e.target.value
                                      }
                                    }
                                  })}
                                  placeholder="X's"
                                />
                              </Box>
                            )}
                            {stage.parameters?.inner_tens && (
                              <Box flex={1}>
                                <FormLabel fontSize="xs">Inner 10s</FormLabel>
                                <Input
                                  type="number"
                                  step="1"
                                  value={editFormData.stage_scores[stage.stage_name]?.inner_tens || ''}
                                  onChange={(e) => setEditFormData({
                                    ...editFormData,
                                    stage_scores: {
                                      ...editFormData.stage_scores,
                                      [stage.stage_name]: {
                                        ...editFormData.stage_scores[stage.stage_name],
                                        inner_tens: e.target.value
                                      }
                                    }
                                  })}
                                  placeholder="10s"
                                />
                              </Box>
                            )}
                            {stage.parameters?.clean_targets && (
                              <Box flex={1}>
                                <FormLabel fontSize="xs">Clean Targets</FormLabel>
                                <Input
                                  type="number"
                                  step="1"
                                  value={editFormData.stage_scores[stage.stage_name]?.clean_targets || ''}
                                  onChange={(e) => setEditFormData({
                                    ...editFormData,
                                    stage_scores: {
                                      ...editFormData.stage_scores,
                                      [stage.stage_name]: {
                                        ...editFormData.stage_scores[stage.stage_name],
                                        clean_targets: e.target.value
                                      }
                                    }
                                  })}
                                  placeholder="Clean"
                                />
                              </Box>
                            )}
                          </HStack>
                        </VStack>
                      ) : (
                        <Input
                          type="number"
                          step="0.01"
                          value={editFormData.stage_scores[stage.stage_name] || ''}
                          onChange={(e) => setEditFormData({
                            ...editFormData,
                            stage_scores: {
                              ...editFormData.stage_scores,
                              [stage.stage_name]: e.target.value
                            }
                          })}
                        />
                      )}
                    </FormControl>
                  ))
                ) : (
                  <Text color="gray.500">No stages defined in format</Text>
                )}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Tiebreaker Data Modal */}
      <Modal isOpen={!!tiebreakerModalResult} onClose={handleCloseTiebreakerModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Tiebreaker Data
            {tiebreakerModalResult && (
              <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                {(tiebreakerModalResult.result_details || tiebreakerModalResult).participant_name}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {tiebreakerFormData && format?.tiebreaker_rules?.enabled && (
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Enter tiebreaker data for this competitor. This data is used to break ties when competitors have the same aggregate score.
                </Text>

                {/* Shootoff Scores */}
                {format.tiebreaker_rules.steps?.filter(s => s && s.type === 'SHOOTOFF').map((step, idx) => (
                  <Box key={idx} p={4} borderWidth="1px" borderRadius="md" bg="purple.50">
                    <FormControl>
                      <FormLabel fontWeight="bold">
                        {step.label || `Shootoff ${step.order}`}
                      </FormLabel>
                      <Text fontSize="sm" color="gray.600" mb={2}>
                        Enter score out of {step.target_count || '?'} targets
                      </Text>
                      <HStack>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max={step.target_count || 999}
                          value={tiebreakerFormData.shootoffs[step.order] || ''}
                          onChange={(e) => setTiebreakerFormData({
                            ...tiebreakerFormData,
                            shootoffs: {
                              ...tiebreakerFormData.shootoffs,
                              [step.order]: e.target.value
                            }
                          })}
                          placeholder="Score"
                        />
                        {step.target_count && (
                          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                            / {step.target_count}
                          </Text>
                        )}
                      </HStack>
                    </FormControl>
                  </Box>
                ))}

                {/* Custom Fields */}
                {format.tiebreaker_rules.steps?.filter(s => s && s.type === 'CUSTOM_FIELD').map((step, idx) => (
                  <Box key={idx} p={4} borderWidth="1px" borderRadius="md" bg="blue.50">
                    <FormControl>
                      <FormLabel fontWeight="bold">
                        {step.label || step.field}
                      </FormLabel>
                      <Input
                        type="number"
                        step="0.01"
                        value={tiebreakerFormData.custom_fields[step.field] || ''}
                        onChange={(e) => setTiebreakerFormData({
                          ...tiebreakerFormData,
                          custom_fields: {
                            ...tiebreakerFormData.custom_fields,
                            [step.field]: e.target.value
                          }
                        })}
                        placeholder={`Enter ${step.label || step.field}`}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {step.direction === 'DESC' ? 'Higher is better' : 'Lower is better'}
                      </Text>
                    </FormControl>
                  </Box>
                ))}

                {format.tiebreaker_rules.steps?.filter(s => s && (s.type === 'SHOOTOFF' || s.type === 'CUSTOM_FIELD')).length === 0 && (
                  <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text fontWeight="bold" mb={2}>Auto-Calculated Tiebreakers</Text>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      This format uses automatically calculated tiebreaker fields. No manual data entry is required.
                    </Text>
                    {format.tiebreaker_rules.steps?.filter(s => s).map((step, idx) => (
                      <Box key={idx} mb={2}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Step {step.order}: {step.label || step.type}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {step.type === 'AGGREGATE_SCORE_FIELD' && `Calculated from stage scores: ${step.field}`}
                          {step.type === 'STAGE_SCORE' && `Uses score from stage: ${step.stage_id || 'N/A'}`}
                          {step.type === 'SHARED_PLACEMENT' && 'Competitors share placement if still tied'}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseTiebreakerModal}>
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={handleSaveTiebreakerData}>
              Save Tiebreaker Data
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
